import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CheckCircle2, Circle, ExternalLink, FileText, Loader2, Sparkles } from "lucide-react";

/**
 * Onboarding checklist + Sample Opportunity view.
 *
 * The panel below the checklist is the first place a new Organization Admin
 * meets the dark analytical canvas: the checklist itself sits on the ivory
 * `/app` surface, and the sample opportunity (once loaded) unfolds inside a
 * dark evidence-map instrument to make the product feel visibly more advanced.
 */

export default function OnboardingPanel({ onOpenTab }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSample, setLoadingSample] = useState(false);
  const [opportunity, setOpportunity] = useState(null);
  const [record, setRecord] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [s, opps, recs] = await Promise.all([
        api.get("/onboarding/status"),
        api.get("/onboarding/opportunities"),
        api.get("/onboarding/decision-records"),
      ]);
      setStatus(s.data);
      const sampleOpp = (opps.data || []).find((o) => o.is_sample) || null;
      setOpportunity(sampleOpp);
      setRecord((recs.data || []).find((r) => r.is_sample) || null);
    } catch (e) {
      toast.error("Unable to load onboarding status.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const loadSample = async () => {
    setLoadingSample(true);
    try {
      const res = await api.post("/onboarding/load-sample");
      setOpportunity(res.data.opportunity);
      setRecord(res.data.decision_record);
      toast.success(res.data.created ? "Sample opportunity loaded." : "Sample opportunity already available.");
      await refresh();
    } catch {
      toast.error("Unable to load sample opportunity.");
    } finally {
      setLoadingSample(false);
    }
  };

  if (loading) {
    return (
      <p className="py-16 text-center font-mono text-[12px] uppercase tracking-widest text-iv-fg-3">
        Loading onboarding…
      </p>
    );
  }
  if (!status) return null;

  return (
    <div data-testid="onboarding-panel">
      {/* Checklist header */}
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <h1 className="font-serif-display text-[34px] md:text-[44px] leading-tight tracking-tight text-iv-fg">
            Onboarding
          </h1>
          <p className="text-[14.5px] text-iv-fg-2 mt-2 max-w-2xl">
            Three steps to reach your first Decision Record for
            {" "}<em className="not-italic text-iv-fg">{status.organization}</em>.
          </p>
        </div>
        <span className={`iv-chip ${status.complete ? "iv-chip--green" : "iv-chip--gold"}`}>
          {status.complete ? "Onboarding complete" : `${status.steps.filter(s => s.done).length} of ${status.steps.length} complete`}
        </span>
      </div>

      {/* Checklist */}
      <ol className="iv-panel divide-y divide-[var(--iv-line)] mb-10" data-testid="onboarding-checklist">
        {status.steps.map((step, i) => (
          <Step
            key={step.key}
            index={i}
            step={step}
            onLoadSample={loadSample}
            loadingSample={loadingSample}
            onOpenTab={onOpenTab}
          />
        ))}
      </ol>

      {/* Sample opportunity — dark analytical canvas */}
      {opportunity && record ? (
        <SampleOpportunityCanvas opportunity={opportunity} record={record} />
      ) : (
        <EmptySamplePrompt onLoad={loadSample} loading={loadingSample} />
      )}
    </div>
  );
}

/* --------------------------------------------------------------- Step -- */

function Step({ index, step, onLoadSample, loadingSample, onOpenTab }) {
  const Icon = step.done ? CheckCircle2 : Circle;
  return (
    <li className="px-6 py-5 flex items-start gap-5" data-testid={`onboarding-step-${step.key}`}>
      <span className="mt-0.5">
        <Icon size={20} className={step.done ? "text-iv-green" : "text-iv-fg-4"} strokeWidth={1.4} />
      </span>
      <div className="flex-1">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <span className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-iv-fg-3 mr-3">
              Step {String(index + 1).padStart(2, "0")}
            </span>
            <span className="font-serif-display text-[19px] text-iv-fg">{step.label}</span>
          </div>
          <span className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-iv-fg-3">
            {step.done ? "Complete" : "Pending"}
          </span>
        </div>
        <p className="text-[13.5px] text-iv-fg-2 mt-1.5 max-w-2xl">{step.detail}</p>
        {!step.done && step.cta && (
          <div className="mt-4">
            {step.cta.action === "load_sample" ? (
              <button
                onClick={onLoadSample}
                disabled={loadingSample}
                data-testid="onboarding-load-sample"
                className="iv-btn iv-btn--primary"
              >
                {loadingSample ? <><Loader2 size={14} className="animate-spin" /> Loading…</> : <><Sparkles size={14} /> {step.cta.label}</>}
              </button>
            ) : step.cta.tab ? (
              <button
                onClick={() => onOpenTab?.(step.cta.tab)}
                data-testid={`onboarding-open-${step.cta.tab}`}
                className="iv-btn"
              >
                {step.cta.label} <ExternalLink size={14} />
              </button>
            ) : null}
          </div>
        )}
      </div>
    </li>
  );
}

function EmptySamplePrompt({ onLoad, loading }) {
  return (
    <div className="iv-panel p-10 text-center" data-testid="onboarding-sample-empty">
      <h3 className="font-serif-display text-[26px] text-iv-fg">A sample opportunity is ready for you.</h3>
      <p className="text-[14px] text-iv-fg-2 mt-2 max-w-xl mx-auto">
        A fictional renewable-energy infrastructure deal — Solaris Mesh — with the documents,
        supporting evidence and open questions your Office would normally review.
      </p>
      <button onClick={onLoad} disabled={loading} className="iv-btn iv-btn--primary mt-6" data-testid="onboarding-load-sample-empty">
        {loading ? <><Loader2 size={14} className="animate-spin" /> Loading…</> : <><Sparkles size={14} /> Load sample opportunity</>}
      </button>
    </div>
  );
}

/* -------------------------------------------------- Sample opportunity -- */

function SampleOpportunityCanvas({ opportunity, record }) {
  const [focused, setFocused] = useState(null);
  const claims = opportunity.claims || [];
  const risks = opportunity.risks || [];
  const openQ = opportunity.open_questions || [];

  return (
    <section className="iv-canvas rounded-[3px]" data-testid="sample-opportunity-canvas">
      {/* Header */}
      <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-[var(--ivc-line)]">
        <div className="flex items-center gap-3">
          <span className="iv-ev-dot iv-ev-dot--verified" />
          <span className="font-mono text-[10.5px] tracking-[0.28em] uppercase text-[var(--ivc-gold-2)]">
            Sample Opportunity · Evidence Map
          </span>
        </div>
        <span className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-[var(--ivc-ivory-3)]">
          {opportunity.documents?.length || 0} documents · {claims.length} claims
        </span>
      </div>

      {/* Opportunity + thesis */}
      <div className="px-6 md:px-10 pt-8 pb-6 grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-8">
          <div className="text-[11px] font-mono tracking-[0.24em] uppercase text-[var(--ivc-ivory-3)]">Opportunity</div>
          <h4 className="font-serif-display text-[26px] md:text-[32px] leading-[1.05] tracking-tight text-[var(--ivc-ivory)] mt-2">
            {opportunity.name}
          </h4>
          <p className="font-serif-display italic text-[16px] leading-relaxed text-[var(--ivc-ivory-2)] mt-4 max-w-[60ch]">
            {opportunity.thesis}
          </p>
        </div>
        <div className="col-span-12 lg:col-span-4 grid grid-cols-2 gap-4 content-start">
          <Fact label="Sector" value={opportunity.sector} />
          <Fact label="Geography" value={opportunity.geography} />
          <Fact label="Instrument" value={opportunity.instrument} />
          <Fact label="Ticket" value={opportunity.size} />
          <Fact label="Hold" value={opportunity.hold} />
          <Fact label="Status" value={record.status} />
        </div>
      </div>

      {/* Documents strip */}
      <div className="px-6 md:px-10 py-4 border-t border-[var(--ivc-line-strong)]">
        <div className="text-[10.5px] font-mono tracking-[0.24em] uppercase text-[var(--ivc-ivory-3)] mb-3">Source documents</div>
        <div className="flex flex-wrap gap-2">
          {(opportunity.documents || []).map((d) => (
            <span
              key={d.id}
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-[var(--ivc-line-warm)] text-[11.5px] text-[var(--ivc-ivory-2)] font-mono"
              style={{ borderRadius: 2 }}
            >
              <FileText size={11} className="text-[var(--ivc-gold-2)]" />
              {d.name}
              <span className="text-[var(--ivc-ivory-4)]">·</span>
              <span className="text-[var(--ivc-ivory-3)]">{d.pages}p</span>
            </span>
          ))}
        </div>
      </div>

      {/* Claims + open questions */}
      <div className="grid grid-cols-12 border-t border-[var(--ivc-line-strong)]">
        <div className="col-span-12 lg:col-span-7 border-b lg:border-b-0 lg:border-r border-[var(--ivc-line-strong)] px-6 md:px-10 py-6">
          <div className="text-[10.5px] font-mono tracking-[0.24em] uppercase text-[var(--ivc-ivory-3)] mb-3">Evidence-linked claims</div>
          <ul>
            {claims.map((c, i) => (
              <li
                key={i}
                data-focus-target
                data-focused={focused === i ? "true" : undefined}
                onMouseEnter={() => setFocused(i)}
                onMouseLeave={() => setFocused(null)}
                className="py-3 border-b border-[var(--ivc-line)] grid grid-cols-[auto_1fr] gap-4"
              >
                <span className="iv-ev-dot iv-ev-dot--supported mt-1.5" />
                <div>
                  <p className="text-[14px] leading-snug text-[var(--ivc-ivory)]">{c.label}</p>
                  <p className="font-mono text-[10.5px] text-[var(--ivc-ivory-3)] mt-1">{c.source}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="col-span-12 lg:col-span-5 px-6 md:px-10 py-6">
          <div className="text-[10.5px] font-mono tracking-[0.24em] uppercase text-[var(--ivc-ivory-3)] mb-3">Open questions</div>
          <ul className="space-y-2.5 mb-6">
            {openQ.map((q, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="iv-ev-dot iv-ev-dot--review mt-1.5" />
                <span className="text-[13.5px] leading-snug text-[var(--ivc-ivory-2)]">{q}</span>
              </li>
            ))}
          </ul>
          <div className="text-[10.5px] font-mono tracking-[0.24em] uppercase text-[var(--ivc-ivory-3)] mb-3">Risks</div>
          <ul className="space-y-2.5">
            {risks.map((r, i) => (
              <li key={i} className="flex items-start gap-3">
                <span
                  className="font-mono text-[9.5px] tracking-[0.14em] uppercase mt-1.5 shrink-0"
                  style={{ color: r.severity === "Material" ? "var(--ivc-amber)" : r.severity === "Elevated" ? "var(--ivc-gold-2)" : "var(--ivc-ivory-3)" }}
                >
                  {r.severity}
                </span>
                <span className="text-[13.5px] leading-snug text-[var(--ivc-ivory-2)]">{r.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Decision record footer */}
      <div className="border-t border-[var(--ivc-line-strong)] px-6 md:px-10 py-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <span className="iv-ev-dot iv-ev-dot--verified" />
          <span className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-[var(--ivc-ivory-3)]">
            Decision Record · {record.status}
          </span>
        </div>
        <span className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--ivc-gold-2)]">
          Ready for committee review
        </span>
      </div>
    </section>
  );
}

function Fact({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--ivc-ivory-3)]">{label}</div>
      <div className="text-[13.5px] leading-tight text-[var(--ivc-ivory)] mt-1">{value}</div>
    </div>
  );
}
